import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { Box } from "@mui/system";
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { getCase, scanningDone, submitCase } from "../../api/caseApi";
import { getTreatmentTypes } from "../../api/treatmentTypesApi";
import { StyledButton } from "../../common/components/Button";
import { GlobalContainer } from "../../common/components/Container";
import { StyledLabel } from "../../common/components/InputLabel";
import { getTreatmentMessage } from "../../common/helpers/getTreatmentMessage";
import { CASE_STATUS, TREATMENT_FILES } from "../../config";
import AdditionalFiles from "./components/AdditionalFiles";
import ArchFormInput from "./components/ArchFormInput";
import AttachmentsInput from "./components/AttachementsInput";
import CanineAndMolarInput from "./components/CanineAndMolarInput";
import CrowdingResolutionInput from "./components/CrowdingResolutionInput";
import FileDropzone from "./components/FileDropzone";
import FuturePlacementInput from "./components/FuturePlacementInput";
import IPRInput from "./components/IPRInput";
import MaintainImproveInput from "./components/MaintainImproveInput";
import MidlineCorrectionInput from "./components/MidlineCorrectionInput";
import SpacingInput from "./components/SpacingInput";
import ToExtractInput from "./components/ToExtractInput";
import TreatmentTypeInput from "./components/TreatmentTypeInput";
import { treatmentPlanSchema } from "./schema/treatmentPlanSchema";
import { FieldsGrid, RequiredFileContainer, Section } from "./styles";
import ConfirmationDialog from "../../common/components/Dialog";

const TreatmentPlan = () => {
  const { caseId } = useParams();
  const [isSubmitting, setSubmitting] = useState(false);
  const [treatments, setTreatments] = useState([]);
  const confirmationSubmit = () => {
    navigate(-1);
  };
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    control,
  } = useForm({
    resolver: yupResolver(treatmentPlanSchema),
  });
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDropAccepted: (files) => {
      const blob = files[0];
      TREATMENT_FILES.forEach((group) => {
        group.files.forEach((file) => {
          setValue(file.name, {
            type: group.type,
            subType: file.subType,
            file: blob,
          });
        });
      });
    },
    onDropRejected: () => {
      alert("Choose 1 file");
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await getCase(caseId);
      const treatmentTypes = await getTreatmentTypes();
      setTreatments(treatmentTypes);
      if (data.data.status !== "For scanning") {
        navigate("/cases");
      }
    };

    fetchData();
  }, [caseId]);

  const handleDrop = (name, file) => {
    setValue(name, file, { shouldValidate: true, shouldTouch: true });
  };

  const handleAdditionalFileDrop = (files) => {
    const formatFiles = files.map((file) => ({
      file: file,
      type: 3,
      subType: 0,
    }));

    setValue("additionalFiles", formatFiles);
  };

  const handleTreatmentPlanSubmit = async (values) => {
    const formData = new FormData();
    const iosFiles = Object.keys(values.iosScan).map(
      (key) => values.iosScan[key]
    );
    const photographyFiles = Object.keys(values.photography).map(
      (key) => values.photography[key]
    );

    const files = iosFiles
      .concat(photographyFiles)
      .concat(values.additionalFiles || []);

    files.forEach((f) => {
      formData.append("files", f.file);
      formData.append("fileTypes", f.type);
      formData.append("fileSubTypes", f.subType);
    });

    const data = { ...values };
    delete data.iosScan;
    delete data.photography;
    delete data.additionalFiles;

    if (data.futurePlacement === "No") {
      data.specificFuturePlacement = null;
    }

    formData.append("treatmentPlan", JSON.stringify(data));
    // formData.append(
    //   "treatmentTypeId",
    //   treatments.find((t) => t.name === data.treatmentType).id
    // );
    formData.append("caseId", caseId);

    try {
      setSubmitting(true);
      await scanningDone(formData);
      setSubmitting(false);
      // TODO: Update
      navigate("/cases/" + caseId, { state: { isFromManageAlina: true } });
    } catch (err) {
      setSubmitting(false);
      setError("submission", {
        type: "custom",
        message: err.response.data.error.message,
      });
      console.log(err);
    }
  };

  // Access nested object with string
  const getValue = function (o, s) {
    s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
    s = s.replace(/^\./, ""); // strip a leading dot
    var a = s.split(".");
    for (var i = 0, n = a.length; i < n; ++i) {
      var k = a[i];
      if (k in o) {
        o = o[k];
      } else {
        return;
      }
    }
    return o;
  };

  return (
    <GlobalContainer
      theme="purple"
      animated={false}
      bgColor={"#F0F0F0"}
      withPadding
    >
      <ConfirmationDialog
        open={showConfirmationDialog}
        handleClose={() => setShowConfirmationDialog(false)}
        submit={confirmationSubmit}
        title={
          "Are you sure you want to cancel? All previously entered data will be deleted."
        }
      />
      <Dialog open={isSubmitting}>
        <Box sx={{ paddingX: 10, paddingY: 10 }}>
          <CircularProgress sx={{ display: "block", margin: "0 auto" }} />
          <Typography sx={{ marginTop: 4, color: grey[600] }}>
            Please wait while we process your request.
          </Typography>
        </Box>
      </Dialog>
      <Box
        component="form"
        disabled
        onSubmit={handleSubmit(handleTreatmentPlanSubmit)}
      >
        <Section>
          <h2>
            Requir
            {/* <Box component="span" {...getRootProps()}>
              <input {...getInputProps()} />e
            </Box> */}
            ed Files
          </h2>
          {TREATMENT_FILES.map((group) => (
            <React.Fragment key={group.name}>
              <h4>{group.name}</h4>
              <RequiredFileContainer>
                {group.files.map((file) => (
                  <FileDropzone
                    type={group.type}
                    key={file.label}
                    label={file.label}
                    name={file.name}
                    control={control}
                    error={getValue(errors, file.name)}
                    onDrop={(f) => {
                      handleDrop(file.name, {
                        file: f,
                        type: group.type,
                        subType: file.subType,
                      });
                    }}
                  />
                ))}
              </RequiredFileContainer>
            </React.Fragment>
          ))}
          <h4>Additional Files</h4>
          <RequiredFileContainer>
            <Box sx={{ padding: "1rem" }}>
              <AdditionalFiles
                control={control}
                name="additionalFiles"
                onDrop={handleAdditionalFileDrop}
              />
            </Box>
          </RequiredFileContainer>
        </Section>

        <Section sx={{ marginBottom: 6 }}>
          <h2>Prescription</h2>

          {/* TODO: Fix margin of h2 being ignored so I added a paddingTop workaround */}
          <Box sx={{ paddingTop: 2 }}>
            <FieldsGrid container rowSpacing={4}>
              {/* Chief complaint / request */}
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(errors.chiefRequest)}>
                  <StyledLabel>Patient Chief Complaint / Request</StyledLabel>
                  <TextField
                    multiline
                    rows={4}
                    name="chiefRequest"
                    error={Boolean(errors.chiefRequest)}
                    helperText={
                      Boolean(errors.chiefRequest) &&
                      errors.chiefRequest.message
                    }
                    FormHelperTextProps={{
                      variant: "standard",
                    }}
                    {...register("chiefRequest")}
                  />
                </FormControl>
              </Grid>

              {/* <TreatmentTypeInput
                treatments={treatments}
                register={register}
                error={Boolean(errors.treatmentType)}
                helperText={
                  Boolean(errors.treatmentType) && errors.treatmentType.message
                }
              /> */}

              <CanineAndMolarInput
                register={register}
                error={Boolean(errors.canineAndMolar)}
                helperText={
                  Boolean(errors.canineAndMolar) &&
                  errors.canineAndMolar.message
                }
              />
              <IPRInput
                register={register}
                error={Boolean(errors.iprPermitted)}
                helperText={
                  Boolean(errors.iprPermitted) && errors.iprPermitted.message
                }
              />
              <AttachmentsInput
                register={register}
                error={Boolean(errors.attachmentsPermitted)}
                helperText={
                  Boolean(errors.attachmentsPermitted) &&
                  errors.attachmentsPermitted.message
                }
              />
              <CrowdingResolutionInput
                register={register}
                error={Boolean(errors.crowdingResolution)}
                helperText={errors.crowdingResolution?.message}
              />

              {/* Row of inputs */}
              <MaintainImproveInput
                register={register}
                error={Boolean(errors.overjet)}
                helperText={errors.overjet?.message}
                label="Overjet"
                name="overjet"
              />
              <MaintainImproveInput
                register={register}
                error={Boolean(errors.overbite)}
                helperText={errors.overbite?.message}
                label="Overbite"
                name="overbite"
              />
              <ArchFormInput
                register={register("archForm")}
                error={Boolean(errors.archForm)}
                helperText={errors.archForm?.message}
              />
              <MaintainImproveInput
                register={register}
                error={Boolean(errors.curveOfSpee)}
                helperText={errors.curveOfSpee?.message}
                label="Curve Of Spee"
                name="curveOfSpee"
              />

              <FuturePlacementInput
                control={control}
                register={register}
                error={Boolean(errors.futurePlacement)}
                specificError={errors.specificFuturePlacement}
                helperText={errors.futurePlacement?.message}
              />

              <MidlineCorrectionInput
                register={register}
                error={{
                  upperMidline: errors.upperMidline,
                  lowerMidline: errors.lowerMidline,
                }}
              />

              <SpacingInput
                register={register}
                control={control}
                error={{
                  closeUpperSpacing: { ...errors.closeUpperSpacing },
                  closeLowerSpacing: { ...errors.closeLowerSpacing },
                }}
                specificError={{
                  specificUpperSpacing: errors.specificUpperSpacing,
                  specificLowerSpacing: errors.specificLowerSpacing,
                }}
              />

              <ToExtractInput
                register={register("teethToBeExtracted")}
                errorMessage={errors.teethToBeExtracted?.message}
              />

              {/* Additional Request*/}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <StyledLabel>Additional Instructions</StyledLabel>
                  <TextField
                    multiline
                    rows={4}
                    error={Boolean(errors.additionalInstructions)}
                    helperText={errors.additionalInstructions?.message}
                    {...register("additionalInstructions")}
                  />
                </FormControl>
              </Grid>
              {errors.submission && (
                <Alert severity="error" sx={{ width: "100%", marginTop: 2 }}>
                  <AlertTitle>Error</AlertTitle>
                  {errors.submission?.message}
                </Alert>
              )}
              <Box marginTop={4} sx={{ width: "100%", textAlign: "right" }}>
                <Box sx={{ display: "inline-block" }}>
                  <StyledButton
                    // variant="contained"
                    color="success"
                    theme="purple"
                    onClick={() => setShowConfirmationDialog(true)}
                  >
                    Cancel
                  </StyledButton>
                  <StyledButton
                    type="submit"
                    // variant="contained"
                    color="success"
                    theme="green"
                    sx={{ marginLeft: 1.5 }}
                  >
                    Finish
                  </StyledButton>
                </Box>
              </Box>
            </FieldsGrid>
          </Box>
        </Section>
      </Box>
    </GlobalContainer>
  );
};

export default TreatmentPlan;
